import React, { useState, useEffect } from 'react';
import { Contact, Horse } from './types';
import { loadContactsData } from './contactsData';
import { loadHorsesData } from './horsesData';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import './PhoneList.css';

// Set up fonts for pdfMake
(pdfMake as any).vfs = pdfFonts;

const PhoneList: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [ownerContacts, setOwnerContacts] = useState<Contact[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<'all' | 'owner' | 'vet' | 'staff'>('all');
  const [isLoading, setIsLoading] = useState(true);

  // Determine if a horse should be visible based on hidden status and contract end date
  const isHorseVisible = (horse: Horse): boolean => {
    // If not hidden, always visible
    if (!horse.hidden) return true;

    // If hidden, check if contract end date is in current month or future
    if (horse.contract?.end_date) {
      const endDate = new Date(horse.contract.end_date);
      const today = new Date();

      // Get year-month for comparison (year * 12 + month gives comparable values)
      const endYearMonth = endDate.getFullYear() * 12 + endDate.getMonth();
      const currentYearMonth = today.getFullYear() * 12 + today.getMonth();

      // Visible if contract ends in current month or later
      return endYearMonth >= currentYearMonth;
    }

    // If hidden and no end date, hide it
    return false;
  };

  useEffect(() => {
    Promise.all([loadContactsData(), loadHorsesData()])
      .then(([contactData, horseData]) => {
        // Load contacts from data
        setContacts(contactData.contacts);

        // Generate owner contacts from active contract data (single source of truth)
        const owners: Contact[] = [];
        const ownerMap = new Map<string, { phone: string; horses: string[] }>();

        horseData.horses.forEach((horse: Horse) => {
          if (!isHorseVisible(horse)) return;
          const ownerName = horse.contract?.owner_name || horse.owner;
          const ownerPhone = horse.contract?.owner_phone || horse.phone || '';
          if (!ownerName || !ownerName.trim()) return;

          const existing = ownerMap.get(ownerName);
          if (existing) {
            existing.horses.push(horse.name);
            if (!existing.phone && ownerPhone) {
              existing.phone = ownerPhone;
            }
          } else {
            ownerMap.set(ownerName, { phone: ownerPhone, horses: [horse.name] });
          }
        });

        ownerMap.forEach((data, name) => {
          owners.push({
            id: `owner/${name}`,
            name: name,
            phone: data.phone,
            category: 'owner',
            notes: data.horses.join(', ')
          });
        });

        setOwnerContacts(owners.sort((a, b) => a.name.localeCompare(b.name)));
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load data:', error);
        alert('Fehler beim Laden der Daten');
        setIsLoading(false);
      });
  }, []);

  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'owner': return 'Pferdebesitzer';
      case 'vet': return 'Tierarzt';
      case 'staff': return 'Personal';
      default: return category;
    }
  };

  const getAllContacts = (): Contact[] => {
    return [...contacts, ...ownerContacts].sort((a, b) => a.name.localeCompare(b.name));
  };

  const filteredContacts = getAllContacts()
    .filter(contact => filterCategory === 'all' || contact.category === filterCategory)
    .filter(contact =>
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contact.notes && contact.notes.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  const getContactsByCategory = () => {
    const all = getAllContacts();
    return {
      vets: all.filter(c => c.category === 'vet').sort((a, b) => a.name.localeCompare(b.name)),
      staff: all.filter(c => c.category === 'staff').sort((a, b) => a.name.localeCompare(b.name)),
      owners: all.filter(c => c.category === 'owner').sort((a, b) => a.name.localeCompare(b.name))
    };
  };

  const handlePrint = () => {
    const categorized = getContactsByCategory();

    const createTableBody = (contacts: Contact[]) => {
      const result: any[] = [];

      result.push([
        {
          text: 'Name',
          style: 'headerCells'
        },
        {
          text: 'Telefon',
          style: 'headerCells'
        },
        {
          text: 'Notizen',
          style: 'headerCells'
        }
      ]);

      contacts.forEach((contact) => {
        result.push([
          {
            text: contact.name,
            style: 'nameColumn'
          },
          {
            text: contact.phone || '-',
            style: 'phoneColumn'
          },
          {
            text: contact.notes || '-',
            style: 'notesColumn'
          }
        ]);
      });

      return result;
    };

    const formatDate = (date: Date): string => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    };

    const docDefinition: any = {
      header: (currentPage: number, pageCount: number) => {
        return {
          margin: [40, 30, 30, 30],
          columns: [
            {
              width: '*',
              text: 'Reitsport Walther & Finger GbR\nBarriser Weg 64\n24787 Fockbek',
              style: 'company'
            },
            {
              width: '150',
              text: formatDate(new Date()) +
                '\nTel.: 01523/3697043\nmail: waltherj@web.de\nSeite ' +
                currentPage.toString() + ' von ' + pageCount,
              style: 'company',
              alignment: 'right'
            }
          ]
        };
      },
      footer: (currentPage: number, pageCount: number) => {
        return {
          margin: [40, 30, 30, 30],
          columns: [
            {
              width: '*',
              text: 'Reitsport Walther & Finger GbR\nGeschäftsführer\nDr. Lutz Finger und Joachim Walther',
              style: 'companySmall'
            },
            {
              width: '*',
              text: 'Raiffeisenbank im Kreis Rendsburg\nIBAN DE72 2169 0020 0005 4678 02\nBIC GENO DEF 1NTO',
              style: 'companySmall'
            },
            {
              width: '*',
              text: 'Finanzamt Rendsburg\nStnr. 028/226/06957',
              style: 'companySmall'
            }
          ]
        };
      },
      content: [
        {
          text: 'Wichtige Telefonnummern\n\n',
          style: 'header'
        },
        {
          text: 'Tierärzte\n',
          style: 'sectionHeader'
        },
        {
          margin: [0, 0, 0, 20],
          table: {
            headerRows: 1,
            widths: [200, 150, '*'],
            body: createTableBody(categorized.vets)
          },
          layout: {
            hLineWidth: (i: number) => (i === 1) ? 3 : 1,
            hLineColor: (i: number) => (i === 1) ? 'black' : 'gray',
            vLineWidth: () => 1,
            vLineColor: 'grey',
            fillColor: (i: number) => (i === 0) ? '#418441' : null
          }
        },
        {
          text: 'Personal\n',
          style: 'sectionHeader'
        },
        {
          margin: [0, 0, 0, 20],
          table: {
            headerRows: 1,
            widths: [200, 150, '*'],
            body: createTableBody(categorized.staff)
          },
          layout: {
            hLineWidth: (i: number) => (i === 1) ? 3 : 1,
            hLineColor: (i: number) => (i === 1) ? 'black' : 'gray',
            vLineWidth: () => 1,
            vLineColor: 'grey',
            fillColor: (i: number) => (i === 0) ? '#418441' : null
          },
          pageBreak: categorized.owners.length > 0 ? 'after' : undefined
        },
        {
          text: 'Pferdebesitzer\n',
          style: 'sectionHeader'
        },
        {
          margin: [0, 0, 0, 20],
          table: {
            headerRows: 1,
            widths: [200, 150, '*'],
            body: createTableBody(categorized.owners)
          },
          layout: {
            hLineWidth: (i: number) => (i === 1) ? 3 : 1,
            hLineColor: (i: number) => (i === 1) ? 'black' : 'gray',
            vLineWidth: () => 1,
            vLineColor: 'grey',
            fillColor: (i: number) => (i === 0) ? '#418441' : null
          }
        }
      ],
      pageSize: 'A4',
      pageMargins: [40, 90, 30, 90],
      styles: {
        company: {
          color: 'black',
          fontSize: 12
        },
        companySmall: {
          color: 'grey',
          fontSize: 8,
          alignment: 'center'
        },
        header: {
          bold: true,
          color: '#ee0000',
          fontSize: 24
        },
        sectionHeader: {
          bold: true,
          color: '#00695c',
          fontSize: 18,
          margin: [0, 10, 0, 10]
        },
        nameColumn: {
          alignment: 'left',
          margin: [5, 5, 10, 10]
        },
        phoneColumn: {
          alignment: 'left',
          margin: [5, 5, 10, 10],
          bold: true
        },
        notesColumn: {
          alignment: 'left',
          margin: [5, 5, 10, 10],
          italics: true
        },
        headerCells: {
          bold: true,
          color: 'white'
        }
      }
    };

    pdfMake.createPdf(docDefinition).open();
  };

  if (isLoading) {
    return (
      <div className="phone-list">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Lade Kontakte...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="phone-list">
      <header className="header">
        <h2>Telefon Liste</h2>
        <h4>
          Wichtige Kontakte: Tierärzte, Personal und Pferdebesitzer
        </h4>
      </header>

      <div className="controls">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Suche nach Name, Telefon oder Notiz..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="control-buttons">
          <label>
            Kategorie:
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value as any)}>
              <option value="all">Alle ({getAllContacts().length})</option>
              <option value="vet">Tierärzte ({getContactsByCategory().vets.length})</option>
              <option value="staff">Personal ({getContactsByCategory().staff.length})</option>
              <option value="owner">Pferdebesitzer ({getContactsByCategory().owners.length})</option>
            </select>
          </label>
          <button onClick={handlePrint} className="btn-print">
            📄 Drucken
          </button>
        </div>
      </div>

      <div className="contacts-container">
        {filterCategory === 'all' && (
          <>
            <section className="contact-section">
              <h3>Tierärzte ({getContactsByCategory().vets.length})</h3>
              <table className="contacts-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Telefon</th>
                    <th>Notizen</th>
                  </tr>
                </thead>
                <tbody>
                  {getContactsByCategory().vets.map((contact) => (
                    <tr key={contact.id}>
                      <td>{contact.name}</td>
                      <td>
                        {contact.phone && (
                          <a href={`tel:${contact.phone}`} className="phone-link">
                            {contact.phone}
                          </a>
                        )}
                      </td>
                      <td>{contact.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="contact-section">
              <h3>Personal ({getContactsByCategory().staff.length})</h3>
              <table className="contacts-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Telefon</th>
                    <th>Notizen</th>
                  </tr>
                </thead>
                <tbody>
                  {getContactsByCategory().staff.map((contact) => (
                    <tr key={contact.id}>
                      <td>{contact.name}</td>
                      <td>
                        {contact.phone && (
                          <a href={`tel:${contact.phone}`} className="phone-link">
                            {contact.phone}
                          </a>
                        )}
                      </td>
                      <td>{contact.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="contact-section">
              <h3>Pferdebesitzer ({getContactsByCategory().owners.length})</h3>
              <table className="contacts-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Telefon</th>
                    <th>Notizen</th>
                  </tr>
                </thead>
                <tbody>
                  {getContactsByCategory().owners.map((contact) => (
                    <tr key={contact.id}>
                      <td>{contact.name}</td>
                      <td>
                        {contact.phone && (
                          <a href={`tel:${contact.phone}`} className="phone-link">
                            {contact.phone}
                          </a>
                        )}
                      </td>
                      <td>{contact.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}

        {filterCategory !== 'all' && (
          <section className="contact-section">
            <h3>{getCategoryLabel(filterCategory)} ({filteredContacts.length})</h3>
            <table className="contacts-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Telefon</th>
                  <th>Notizen</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact) => (
                  <tr key={contact.id}>
                    <td>{contact.name}</td>
                    <td>
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="phone-link">
                          {contact.phone}
                        </a>
                      )}
                    </td>
                    <td>{contact.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
};

export default PhoneList;
